import React, { useEffect, useState } from 'react';

const SRC_KEY = 'norsk_news_word_source';

export default function NewsSection({ uiLang, t, knownWords, onClose }) {
  const [items, setItems] = useState(null);
  const [updated, setUpdated] = useState(null);
  const [tip, setTip] = useState(null);
  const [source, setSource] = useState(() => {
    try { return localStorage.getItem(SRC_KEY) === 'public' ? 'public' : 'personal'; } catch (e) { return 'personal'; }
  });
  const [publicWords, setPublicWords] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    fetch('/api/ai/news?lang=' + encodeURIComponent(uiLang || 'ru'), { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled) return;
        if (d && Array.isArray(d.items)) { setItems(d.items); setUpdated(d.updated); }
        else { setItems([]); }
      })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, [uiLang]);

  useEffect(() => {
    if (source !== 'public' || publicWords !== null) return;
    let cancelled = false;
    fetch('/api/ai/dict-index', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled) return;
        const set = new Set();
        if (d && Array.isArray(d.words)) {
          d.words.forEach((w) => {
            if (w.n) set.add(normNo(w.n));
            if (Array.isArray(w.f)) w.f.forEach((f) => set.add(normNo(f)));
          });
        }
        setPublicWords(set);
      })
      .catch(() => { if (!cancelled) setPublicWords(new Set()); });
    return () => { cancelled = true; };
  }, [source, publicWords]);

  const chooseSource = (s) => {
    setSource(s);
    try { localStorage.setItem(SRC_KEY, s); } catch (e) {}
  };

  let activeWords = knownWords || new Set();
  if (source === 'public' && publicWords) {
    activeWords = new Set(activeWords);
    publicWords.forEach((w) => activeWords.add(w));
  }

  const showTip = (ru, e) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    setTip({ text: ru, x: Math.round(r.left + r.width / 2), y: Math.round(r.top) });
  };

  return (
    <div className="news-overlay" onClick={() => { setTip(null); onClose(); }}>
      <div className="news-panel" onClick={(e) => { e.stopPropagation(); setTip(null); }}>
        <div className="news-head">
          <h2 className="news-title">{t('news')}</h2>
          <div className="news-head-right">
            <div className="news-src-toggle" role="group" aria-label="Источник слов">
              <button type="button" className={"news-src-seg" + (source === 'personal' ? ' active' : '')} onClick={() => chooseSource('personal')} title="Мой словарь" aria-label="Мой словарь">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
              </button>
              <button type="button" className={"news-src-seg" + (source === 'public' ? ' active' : '')} onClick={() => chooseSource('public')} title="Публичная база" aria-label="Публичная база">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
              </button>
            </div>
            <button type="button" className="news-close" aria-label="Закрыть" onClick={onClose}>×</button>
          </div>
        </div>
        {items === null && <p className="news-info">{t('newsLoading')}</p>}
        {items !== null && items.length === 0 && <p className="news-info">{t('newsEmpty')}</p>}
        {items !== null && items.length > 0 && (
          <div className="news-list">
            {items.map((n, i) => (
              <article className="news-card" key={i}>
                <h3 className="news-card-title">{n.title}</h3>
                {n.title_translation && <p className="news-card-subtitle">{n.title_translation}</p>}
                <p className="news-card-text">{renderTagged(n.retelling, activeWords, showTip)}</p>
                <a className="news-card-link" href={n.link} target="_blank" rel="noopener noreferrer">{t('newsRead')}{n.source ? ' · ' + n.source : ''}</a>
              </article>
            ))}
          </div>
        )}
      </div>
      {tip && (
        <div className="news-tip" style={{ left: tip.x + 'px', top: tip.y + 'px' }}>{tip.text}</div>
      )}
    </div>
  );
}

function normNo(s) {
  return String(s || '').trim().toLowerCase().replace(/^(en|ei|et|å)\s+/i, '');
}

function renderTagged(text, knownWords, onTap) {
  if (!text) return null;
  const re = /\[([^|\]]+)\|([^\]]+)\]/g;
  const out = [];
  let last = 0, m, k = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const ru = m[1];
    const no = (m[2] || '').trim();
    if (no && no.toLowerCase() !== 'null' && knownWords && knownWords.has(normNo(no))) {
      out.push(React.createElement('span', { key: k++, className: 'news-no', title: ru, onClick: onTap ? (e) => onTap(ru, e) : undefined }, no));
    } else {
      out.push(ru);
    }
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
